"use client";

import { useAuth } from "@/components/features/auth/hooks/use-auth";
import { LoginPromptPopover } from "@/components/login-prompt";
import { ReactElement, cloneElement, useState } from "react";

interface AuthRequiredWrapperProps {
  children: ReactElement;
  message?: string;
  onAuthRequired?: () => void;
  disabled?: boolean;
}

/**
 * A wrapper component that intercepts clicks on child elements and shows a login prompt
 * if the user is not authenticated. If the user is authenticated, it passes through
 * the original click handler.
 */
export function AuthRequiredWrapper({
  children,
  message = "Please sign in to continue",
  onAuthRequired,
  disabled = false,
}: AuthRequiredWrapperProps) {
  const { isAuthenticated } = useAuth();
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);

  const handleClick = (originalHandler?: () => void | Promise<void>) => {
    return (event?: React.MouseEvent) => {
      if (disabled) return;

      if (!isAuthenticated) {
        event?.preventDefault();
        event?.stopPropagation();
        setLoginPromptOpen(true);
        onAuthRequired?.();
        return;
      }

      // User is authenticated, call the original handler
      originalHandler?.();
    };
  };

  // If user is authenticated or component is disabled, render children as-is
  if (isAuthenticated || disabled) {
    return children;
  }

  // Clone the child element and wrap its onClick handler
  const wrappedChild = cloneElement(children, {
    onClick: handleClick((children.props as any)?.onClick),
  } as any);

  return (
    <LoginPromptPopover
      open={loginPromptOpen}
      onOpenChange={setLoginPromptOpen}
      message={message}
    >
      {wrappedChild}
    </LoginPromptPopover>
  );
}
